import { useAlert, useLoadingOverlay } from '@genial/components';
import { CustomerEntity, Service } from '@genial/services';
import { FinanceEnumsFileSharingTypeEnum } from '@genial/services/dist/models';
import { ErrorResponse } from '@genial/services/src';
import { useNavigation } from '@react-navigation/native';
import * as ExpoFileSystem from 'expo-file-system';
import { Platform, Share } from 'react-native';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { useToastCreator } from '../../hooks/useToastCreator';
import { SignedInNavigationScreenNavigationProp } from '../../routes/types';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';
import { changeProductStatus } from '../product/selectors';

import {
  customerBySearchTermParamsState,
  customerItemState,
  customerParamsState,
  customerStatusState,
} from './atom';
import {
  changeCustomerStatus,
  customerBySearchTermParams,
  customerItem,
  customerParams,
  updateIsExportingPdfForCustomer,
  updateListAllCustomersSelector,
  updateListCustomersBySearchTermSelector,
} from './selector';
import {
  CustomerBySearchTermParams,
  CustomerParams,
  CustomerStatus,
  SelectedCustomer,
} from './types';

export const useCustomerActions = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const setProductStatus = useSetRecoilState(changeProductStatus);
  const { alert } = useAlert();
  const { showToast } = useToastCreator('download-income-statement-file-toast');

  const { show: showNewApplicationLoadingOverlay, hide: hideNewApplicationLoadingOverlay } =
    useLoadingOverlay();

  const selectedAdvisor = useRecoilValue(advisorIdToApisSelector);
  const selectedPartner = useRecoilValue(partnerIdToApisSelector);
  const setIsExportingPdfForCustomer = useSetRecoilState(updateIsExportingPdfForCustomer);
  const setCustomerItem = useSetRecoilState(customerItem);
  const setCustomerStatus = useSetRecoilState(changeCustomerStatus);
  const setCustomerParams = useSetRecoilState(customerParams);
  const setCustomerBySearchTermParams = useSetRecoilState(customerBySearchTermParams);
  const setListAllCustomers = useSetRecoilState(updateListAllCustomersSelector);
  const setListCustomersBySearchTerm = useSetRecoilState(updateListCustomersBySearchTermSelector);

  const clearCustomerItem = useResetRecoilState(customerItemState);
  const resetCustomerStatus = useResetRecoilState(customerStatusState);
  const resetCustomerParams = useResetRecoilState(customerParamsState);
  const resetCustomerBySearchTermParams = useResetRecoilState(customerBySearchTermParamsState);

  const updateCustomerStatus = (data: CustomerStatus) => {
    setCustomerStatus(data);
  };

  const handleSelectCustomerItem = (item: SelectedCustomer) => {
    return setCustomerItem(item);
  };

  const resetCustomer = () => {
    clearCustomerItem();
    resetCustomerStatus();
    resetCustomerParams();
    resetCustomerBySearchTermParams();
    updateListCustomersBySearchTerm([]);
  };

  const updateCustomerParams = (value: Partial<CustomerParams>) => {
    setCustomerParams(value);
  };

  const updateCustomerBySearchTermParams = (value: Partial<CustomerBySearchTermParams>) => {
    setCustomerBySearchTermParams(value);
  };

  const resetCustomerAndSearchTermParams = () => {
    resetCustomerParams();
    resetCustomerBySearchTermParams();
  };

  const updateListAllCustomers = (value: CustomerEntity[]) => {
    setListAllCustomers(value);
  };

  const updateListCustomersBySearchTerm = (value: CustomerEntity[]) => {
    setListCustomersBySearchTerm(value);
  };

  const downloadIncomeStatementFile = async (
    cpfCnpj: string,
    yearReference: number,
    sharingType: FinanceEnumsFileSharingTypeEnum = 'Download'
  ) => {
    setIsExportingPdfForCustomer(true);

    const handleDownloadError = (error?: ErrorResponse) => {
      showToast(
        'error',
        'Não foi possível processar sua solicitação.',
        error?.errorMessage || 'Não há informações disponíveis no momento.'
      );
    };

    try {
      const {
        data: { file, notificationSent },
      } = await Service.Finance.V1.getIncomeTaxByDownloadZipCpfCnpj({
        cpfCnpj,
        yearReference,
        sharingType,
        screenName: 'anywhere',
      });

      if (file === '' && notificationSent) {
        showToast(
          'success',
          'Extrato enviado com sucesso.',
          'O extrato foi enviado para o email do cliente.'
        );
        return;
      }

      if (!file || file === '') {
        handleDownloadError();
        return;
      }

      if (Platform.OS === 'web') {
        // file is base64 encoded, we must convert it to binary
        const byteCharacters = atob(file);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `IR_${yearReference}.zip`);
        document.body.appendChild(link);
        link.click();
      } else {
        const uri = `${ExpoFileSystem.cacheDirectory}IR_${yearReference}.zip`;
        await ExpoFileSystem.writeAsStringAsync(uri, file, {
          encoding: ExpoFileSystem.EncodingType.Base64,
        });

        await Share.share({
          url: uri,
          title: `IR_${yearReference}.zip`,
        });
      }
    } catch (error) {
      handleDownloadError(error as ErrorResponse);
    } finally {
      setIsExportingPdfForCustomer(false);
    }
  };

  const createNewApplication = async (cpfCnpj: string) => {
    showNewApplicationLoadingOverlay();

    const handleCustomerError = () => {
      hideNewApplicationLoadingOverlay();
      alert({
        description: 'Ocorreu um erro ao criar uma nova ordem. Tente novamente mais tarde.',
      });
    };

    try {
      const { data: customers } = await Service.Customer.V1.getByCpfCnpj({
        cpfCnpj,
        advisorId: selectedAdvisor,
        partnerId: selectedPartner,
        screenName: 'anywhere',
      });

      if (!customers) {
        handleCustomerError();

        return;
      }

      const customer = customers[0].customer;

      handleSelectCustomerItem(customer);
      updateCustomerStatus({ label: 'Cliente selecionado', status: 'completed' });
      setProductStatus({
        label: 'Selecione o produto',
        status: 'opened',
      });

      navigation.navigate('NewApplication');
      hideNewApplicationLoadingOverlay();
    } catch (_error) {
      handleCustomerError();
    }
  };

  return {
    updateCustomerStatus,
    handleSelectCustomerItem,
    resetCustomer,
    updateCustomerParams,
    updateCustomerBySearchTermParams,
    resetCustomerAndSearchTermParams,
    updateListAllCustomers,
    updateListCustomersBySearchTerm,
    downloadIncomeStatementFile,
    createNewApplication,
  };
};
